import React, { useState, useEffect } from 'react';
import { getSupplierInvoices, saveSupplierInvoice, deleteSupplierInvoice } from '../../data/supplierInvoices';
import { printFactureFournisseur, getCompanyInfo, buildFactureFournisseurHtml } from '../../data/supplierDocuments';

export default function SupplierInvoiceTab({ suppliers, catalogProducts, message, setMessage, initialInvoiceFromPurchase }) {
  const [invoices, setInvoices] = useState([]);
  const [mode, setMode] = useState('list'); // 'list' or 'form'
  const [form, setForm] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  
  useEffect(() => {
    setInvoices(getSupplierInvoices());
  }, []);

  useEffect(() => {
    if (initialInvoiceFromPurchase) {
      setForm(initialInvoiceFromPurchase);
      setMode('form');
    }
  }, [initialInvoiceFromPurchase]);

  const formatMoney = (val) => Number(val || 0).toFixed(2) + ' €';

  const handleDelete = (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    deleteSupplierInvoice(id);
    setInvoices(getSupplierInvoices());
    setMessage('Facture supprimée.');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleMarkPaid = (inv) => {
    const updated = { ...inv, status: 'paid', paidDate: new Date().toISOString() };
    saveSupplierInvoice(updated);
    setInvoices(getSupplierInvoices());
    setMessage('Facture marquée comme payée.');
    setTimeout(() => setMessage(''), 3000);
  };

  const handlePrint = (inv) => {
    const supplier = suppliers.find(s => s.id === inv.supplierId);
    printFactureFournisseur(inv, supplier);
  };

  const handlePreview = (inv) => {
    const supplier = suppliers.find(s => s.id === inv.supplierId);
    const html = buildFactureFournisseurHtml(inv, supplier, getCompanyInfo());
    setPreviewHtml(html);
  };

  const handleEdit = (inv) => {
    setForm({ ...inv });
    setMode('form');
  };

  // Form handling
  const handleSaveForm = (e) => {
    e.preventDefault();
    if (!form.supplierId || !form.invoiceNumber) {
      alert("Le fournisseur et le numéro de facture sont requis.");
      return;
    }
    
    // Recalculate totals just in case
    let totalHT = 0;
    let tvaTotal = 0;
    form.items.forEach(item => {
      const lineHT = item.quantity * item.unitPriceHT;
      totalHT += lineHT;
      tvaTotal += lineHT * (item.tvaRate / 100);
    });
    
    const invoiceData = {
      ...form,
      id: form.id || Date.now().toString(),
      totalHT,
      tvaTotal,
      totalTTC: totalHT + tvaTotal,
      updatedAt: new Date().toISOString()
    };
    if (!invoiceData.createdAt) invoiceData.createdAt = new Date().toISOString();
    
    saveSupplierInvoice(invoiceData);
    setInvoices(getSupplierInvoices());
    setMode('list');
    setForm(null);
    setMessage('Facture enregistrée.');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleItemChange = (idx, field, value) => {
    const newItems = [...form.items];
    if (field === 'productId') {
      newItems[idx].productId = value;
      newItems[idx].description = catalogProducts.find(p => p.id === value)?.name || '';
    } else {
      newItems[idx][field] = field === 'description' ? value : Number(value);
    }
    setForm({ ...form, items: newItems });
  };

  const handleAddItem = () => {
    setForm({ ...form, items: [...form.items, { productId: '', description: '', quantity: 1, unitPriceHT: 0, tvaRate: 20 }] });
  };

  const handleRemoveItem = (idx) => {
    const newItems = [...form.items];
    newItems.splice(idx, 1);
    setForm({ ...form, items: newItems });
  };

  // Calculate stats
  const stats = invoices.reduce((acc, inv) => {
    acc.total++;
    if (inv.status === 'pending' || inv.status === 'overdue') acc.pending++;
    if (inv.status === 'paid') acc.paid++;
    acc.totalAmount += inv.totalTTC;
    return acc;
  }, { total: 0, pending: 0, paid: 0, totalAmount: 0 });

  if (mode === 'form' && form) {
    let currentTotalHT = 0;
    let currentTvaTotal = 0;
    form.items.forEach(item => {
      const lineHT = item.quantity * item.unitPriceHT;
      currentTotalHT += lineHT;
      currentTvaTotal += lineHT * (item.tvaRate / 100);
    });

    return (
      <div className="card shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{form.id ? 'Modifier Facture' : 'Nouvelle Facture'}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setMode('list'); setForm(null); }}>Retour</button>
        </div>
        <div className="card-body">
          <form onSubmit={handleSaveForm}>
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label">Fournisseur</label>
                <select className="form-select" value={form.supplierId} onChange={e => setForm({...form, supplierId: e.target.value})} required>
                  <option value="">Sélectionner...</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">N° Facture</label>
                <input type="text" className="form-control" value={form.invoiceNumber} onChange={e => setForm({...form, invoiceNumber: e.target.value})} required />
              </div>
              <div className="col-md-2">
                <label className="form-label">Date Facture</label>
                <input type="date" className="form-control" value={form.date ? form.date.substring(0,10) : ''} onChange={e => setForm({...form, date: e.target.value})} required />
              </div>
              <div className="col-md-2">
                <label className="form-label">Date Échéance</label>
                <input type="date" className="form-control" value={form.dueDate ? form.dueDate.substring(0,10) : ''} onChange={e => setForm({...form, dueDate: e.target.value})} />
              </div>
              <div className="col-12">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows="2" value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})}></textarea>
              </div>
            </div>

            <table className="table table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th>Produit / Description</th>
                  <th>Qté</th>
                  <th>Prix Unitaire HT</th>
                  <th>TVA %</th>
                  <th>Total HT</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <select className="form-select form-select-sm mb-1" value={item.productId || ''} onChange={e => handleItemChange(idx, 'productId', e.target.value)}>
                        <option value="">Autre (Saisir description)</option>
                        {catalogProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <input type="text" className="form-control form-control-sm" placeholder="Description libre" value={item.description || ''} onChange={e => handleItemChange(idx, 'description', e.target.value)} />
                    </td>
                    <td><input type="number" className="form-control form-control-sm" min="1" value={item.quantity} onChange={e => handleItemChange(idx, 'quantity', e.target.value)} required /></td>
                    <td><input type="number" step="0.01" className="form-control form-control-sm" value={item.unitPriceHT} onChange={e => handleItemChange(idx, 'unitPriceHT', e.target.value)} required /></td>
                    <td>
                      <select className="form-select form-select-sm" value={item.tvaRate} onChange={e => handleItemChange(idx, 'tvaRate', e.target.value)}>
                        <option value="5.5">5.5%</option>
                        <option value="10">10%</option>
                        <option value="20">20%</option>
                      </select>
                    </td>
                    <td>{formatMoney(item.quantity * item.unitPriceHT)}</td>
                    <td><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveItem(idx)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="btn btn-sm btn-outline-primary mb-3" onClick={handleAddItem}>+ Ajouter Ligne</button>
            
            <div className="row justify-content-end">
              <div className="col-md-4">
                <table className="table table-sm">
                  <tbody>
                    <tr><td>Total HT:</td><td className="text-end">{formatMoney(currentTotalHT)}</td></tr>
                    <tr><td>TVA:</td><td className="text-end">{formatMoney(currentTvaTotal)}</td></tr>
                    <tr><th>Total TTC:</th><th className="text-end">{formatMoney(currentTotalHT + currentTvaTotal)}</th></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-end mt-3">
              <button type="button" className="btn btn-secondary me-2" onClick={() => { setMode('list'); setForm(null); }}>Annuler</button>
              <button type="submit" className="btn btn-primary">Enregistrer</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <span className="badge bg-warning text-dark">En attente</span>;
      case 'paid': return <span className="badge bg-success">Payée</span>;
      case 'overdue': return <span className="badge bg-danger">En retard</span>;
      case 'disputed': return <span className="badge bg-secondary">Litige</span>;
      default: return <span className="badge bg-secondary">{status}</span>;
    }
  };

  return (
    <div>
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-white bg-secondary mb-3">
            <div className="card-body">
              <h5 className="card-title">Total Factures</h5>
              <h3>{stats.total}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-dark bg-warning mb-3">
            <div className="card-body">
              <h5 className="card-title">En attente</h5>
              <h3>{stats.pending}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-white bg-success mb-3">
            <div className="card-body">
              <h5 className="card-title">Payées</h5>
              <h3>{stats.paid}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-white bg-primary mb-3">
            <div className="card-body">
              <h5 className="card-title">Montant Total TTC</h5>
              <h3>{formatMoney(stats.totalAmount)}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3 text-end">
        <button className="btn btn-primary" onClick={() => { 
          setForm({ supplierId: '', invoiceNumber: '', date: new Date().toISOString(), dueDate: '', items: [], status: 'pending' }); 
          setMode('form'); 
        }}>+ Nouvelle facture</button>
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>N° Facture</th>
              <th>Fournisseur</th>
              <th>Date</th>
              <th>Montant TTC</th>
              <th>Statut</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.sort((a,b) => new Date(b.date) - new Date(a.date)).map(inv => {
              const supplier = suppliers.find(s => s.id === inv.supplierId);
              return (
                <tr key={inv.id}>
                  <td><strong>{inv.invoiceNumber}</strong></td>
                  <td>{supplier ? supplier.name : 'Inconnu'}</td>
                  <td>{new Date(inv.date).toLocaleDateString()}</td>
                  <td>{formatMoney(inv.totalTTC)}</td>
                  <td>{getStatusBadge(inv.status)}</td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => handlePreview(inv)}>👁️</button>
                    <button className="btn btn-sm btn-outline-info me-1" onClick={() => handlePrint(inv)}>🖨️</button>
                    <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleEdit(inv)}>✏️</button>
                    {inv.status !== 'paid' && (
                      <button className="btn btn-sm btn-success me-1" onClick={() => handleMarkPaid(inv)}>✅ Payée</button>
                    )}
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(inv.id)}>🗑️</button>
                  </td>
                </tr>
              );
            })}
            {invoices.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center text-muted py-4">Aucune facture trouvée.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {previewHtml && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Aperçu de la Facture</h5>
                <button type="button" className="btn-close" onClick={() => setPreviewHtml('')}></button>
              </div>
              <div className="modal-body p-0">
                <iframe title="Facture Preview" srcDoc={previewHtml} style={{ width: '100%', height: '500px', border: 'none' }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
